import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { Brand } from "@/components/Brand";
import { ProductLineSwitcher } from "@/components/ProductLineSwitcher";
import { SideNav } from "@/components/SideNav";
import { BrokerageMenu } from "@/components/BrokerageMenu";
import { NotificationBell } from "@/components/NotificationBell";
import { HeaderSearch } from "@/components/HeaderSearch";
import { SiteFooter } from "@/components/SiteFooter";
import { useSupportChat } from "@/components/SupportChatProvider";
import { isExchangePath, isWidePath } from "@/platform/productLine";
import {
  getOwnProfile,
  getOwnBrokerage,
  hasCapability,
  type BrokerProfile,
  type BrokerBrokerage,
} from "@/lib/brokerAuth";

// The authenticated app shell (BRK-S035): a left nav carrying the broker's
// identity + primary destinations, and a top bar whose right side shows the
// brokerage name and opens the account menu. Mounted as a layout route around
// the whole protected group, so it also wraps the security-onboarding gate — an
// authenticated broker always sees this chrome; the gate only swaps <main>.
//
// BrokerIdentity (from /me) has no display name, so the shell reads the broker's
// name from GET /api/broker/profile and the brokerage name from
// GET /api/broker/brokerage. Both reads fail soft (never white-screen, #7): the
// name falls back to the email identity, the brokerage to a neutral label.
//
// Mobile-first: below lg the sidebar is an off-canvas drawer toggled by the
// header hamburger; at lg+ it's a persistent column.
export function PortalShell() {
  const { identity, authedRequest } = useAuth();
  const [profile, setProfile] = useState<BrokerProfile | null>(null);
  const [brokerage, setBrokerage] = useState<BrokerBrokerage | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  // The Exchange landing owns a full-bleed dark canvas; every other working
  // screen keeps the light, padded content column.
  const pathname = useLocation().pathname;
  // Full-bleed, self-padded landing pages (own their own bg + padding) — the
  // list of them is owned with the rest of the route→platform knowledge.
  const isExchange = isExchangePath(pathname);
  // Wide pages (lists, BaseForm records) fill the content area instead of the
  // reading-width cap.
  const isWide = isWidePath(pathname);
  const canViewAccounts = hasCapability(identity, "account:view");
  const { describeBroker } = useSupportChat();

  useEffect(() => {
    let active = true;
    getOwnProfile(authedRequest)
      .then((p) => {
        if (active) setProfile(p);
      })
      .catch(() => {
        /* fail soft — fall back to the email identity */
      });
    getOwnBrokerage(authedRequest)
      .then((b) => {
        if (active) setBrokerage(b);
      })
      .catch(() => {
        /* fail soft — fall back to the neutral label */
      });
    return () => {
      active = false;
    };
  }, [authedRequest]);

  // Resolved-or-null first, then the display fallbacks. Live chat wants to know
  // the difference: a real name identifies the broker to the agent, whereas the
  // fallbacks ("Your brokerage", the raw email) are chrome, and shipping a
  // placeholder as an identity would tell the agent something untrue.
  const resolvedName = profile
    ? `${profile.firstName} ${profile.lastName}`.trim()
    : null;
  const resolvedBrokerage = brokerage?.name ?? null;
  const displayName = resolvedName ?? identity?.email ?? "";
  const brokerageName = resolvedBrokerage ?? "Your brokerage";

  // Hand the support widget the names this shell already read (BRK-S136), so an
  // agent opens the conversation knowing who is asking without a second fetch.
  useEffect(() => {
    describeBroker({
      displayName: resolvedName,
      brokerageName: resolvedBrokerage,
    });
  }, [describeBroker, resolvedName, resolvedBrokerage]);

  return (
    <div
      data-portal-shell
      className="flex min-h-full flex-col bg-brk-bg text-brk-text"
    >
      {/* At lg+ the header, sidebar and footer are fixed chrome (BRK-S228/S229):
          the header and footer take their heights from --brk-header-h and
          --brk-footer-h (index.css), and the sidebar sticks between them. */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-brk-ink text-white backdrop-blur lg:h-[var(--brk-header-h)]">
        <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6 sm:py-[1.3rem] lg:h-full lg:py-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={navOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={navOpen}
              aria-controls="portal-sidenav"
              onClick={() => setNavOpen((prev) => !prev)}
              className="-ml-1 inline-flex items-center justify-center rounded-md p-2 text-white transition-colors hover:bg-white/10 lg:hidden"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <Brand size="sm" />
            <ProductLineSwitcher />
          </div>
          {canViewAccounts && (
            <div className="hidden flex-1 justify-center px-4 md:flex">
              <HeaderSearch />
            </div>
          )}
          <div className="flex items-center gap-2 sm:gap-3">
            <BrokerageMenu brokerageName={brokerageName} />
            {/* In-app inbox (BRK-S157): a real UNSEEN badge over a link to
                /notifications, gated on notification:view. */}
            <NotificationBell />
            {/* Broker avatar (identity lives in the sidebar; this mirrors it). */}
            <span
              aria-hidden="true"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/10 text-xs font-semibold text-white"
            >
              {displayName
                .split(/\s+/)
                .slice(0, 2)
                .map((p) => p[0]?.toUpperCase() ?? "")
                .join("")}
            </span>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Dimmed overlay behind the mobile drawer (below lg only). */}
        {navOpen && (
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setNavOpen(false)}
            className="fixed inset-0 z-30 bg-brk-text/40 lg:hidden"
          />
        )}

        <aside
          id="portal-sidenav"
          className={`fixed inset-y-0 left-0 z-40 w-64 border-r border-brk-border bg-brk-panel transition-transform duration-200 lg:sticky lg:bottom-auto lg:top-[var(--brk-header-h)] lg:z-auto lg:h-[calc(100dvh_-_var(--brk-header-h)_-_var(--brk-footer-h))] lg:shrink-0 lg:self-start ${
            navOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
        >
          <SideNav
            identity={identity}
            displayName={displayName}
            brokerageName={brokerageName}
            onNavigate={() => setNavOpen(false)}
          />
        </aside>

        {isExchange ? (
          // The Exchange page owns a full-width canvas (no padding cap).
          <main className="min-w-0 flex-1 bg-brk-bg">
            <Outlet />
          </main>
        ) : (
          <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 sm:py-12">
            <div
              className={`mx-auto w-full ${isWide ? "max-w-none" : "max-w-5xl"}`}
            >
              <Outlet />
            </div>
          </main>
        )}
      </div>

      <SiteFooter sticky />
    </div>
  );
}
